import { PDFDocument, StandardFonts, rgb, PDFName, PDFString, PDFHexString, PDFArray, PDFDict, PDFNumber } from 'pdf-lib';

export interface PdfGenerationOptions {
  title: string;
  content: string;
  companyInfo?: {
    name: string;
    address: string;
  };
  signatureImage?: string;
  date?: Date;
}

export interface DeletionCertificateData {
  receiptId: string;
  businessName?: string;
  businessAddress?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerCreatedAt?: string;
  deletedAt: string;
  deletedAppointments: number;
  deletedConversations: number;
}

const pdfService = {
  generateContractPDF: async (options: PdfGenerationOptions): Promise<Uint8Array> => {
    try {
      const pdfDoc = await PDFDocument.create();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const PAGE_WIDTH = 595;
      const PAGE_HEIGHT = 842;
      const MARGIN = 50;
      const MAX_WIDTH = PAGE_WIDTH - 2 * MARGIN;
      const FOOTER_Y = 30;
      const BOTTOM_MARGIN = 60;

      // --- Helper: add a new page with footer ---
      const addNewPage = () => {
        const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        page.drawText(
          'EcomTask UG (haftungsbeschraenkt) | Rauenthaler Strasse 12 | 65197 Wiesbaden',
          { x: MARGIN, y: FOOTER_Y, size: 8, font: helveticaFont, color: rgb(0.5, 0.5, 0.5) }
        );
        return page;
      };

      // --- Helper: wrap text into lines that fit maxWidth ---
      const wrapText = (text: string, font: typeof helveticaFont, fontSize: number, maxW: number): string[] => {
        const sanitized = text.replace(/\t/g, '    ');
        const words = sanitized.split(' ');
        const lines: string[] = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const width = font.widthOfTextAtSize(testLine, fontSize);
          if (width > maxW && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines.length > 0 ? lines : [''];
      };

      // --- Heading detection ---
      const isHeading = (line: string): boolean => {
        const t = line.trim();
        if (!t) return false;
        if (/^(§|Anlage)\s*\d+/i.test(t)) return true;
        if (/^[A-F]\.\s/.test(t)) return true;
        if (/^(Vertrag\s|Add-ons\s)/.test(t)) return true;
        // Known standalone section titles (short, uppercase start, no period mid-sentence)
        const knownHeadings = [
          'Vertragsgegenstand', 'Leistungen von EcomTask', 'Nutzungsumfang und -rechte',
          'Support', 'Service Levels', 'Pflichten und Mitwirkung des Kunden',
          'Gewaehrleistung', 'Haftung', 'Rechtsmaengel; Freistellung',
          'Verguetung und Zahlungsbedingungen', 'Vertragslaufzeit und Beendigung',
          'Datenschutz; Geheimhaltung; Referenzkunde', 'Schlussbestimmungen',
          'Praeambel', 'Anlagenverzeichnis', 'zwischen', 'und',
          'Gewährleistung', 'Rechtsmängel; Freistellung',
          'Vergütung und Zahlungsbedingungen; Preisanpassungen',
          'Präambel',
        ];
        if (knownHeadings.some(h => t === h)) return true;
        // Short lines that look like titles
        if (t.length < 70 && /^[A-ZÄÖÜ]/.test(t) && !t.includes('. ') && !t.startsWith('(') && !t.startsWith('•')) return true;
        return false;
      };

      // State
      let currentPage = addNewPage();
      let yPos = PAGE_HEIGHT - MARGIN;

      const ensureSpace = (needed: number) => {
        if (yPos - needed < BOTTOM_MARGIN) {
          currentPage = addNewPage();
          yPos = PAGE_HEIGHT - MARGIN;
        }
      };

      const drawTextBlock = (text: string, fontSize: number, font: typeof helveticaFont, lineHeight: number, color = rgb(0, 0, 0)) => {
        const wrapped = wrapText(text, font, fontSize, MAX_WIDTH);
        for (const line of wrapped) {
          ensureSpace(lineHeight);
          currentPage.drawText(line, { x: MARGIN, y: yPos, size: fontSize, font, color });
          yPos -= lineHeight;
        }
      };

      // === HEADER ===
      currentPage.drawText('TimeGlobe', {
        x: MARGIN, y: yPos, size: 24, font: helveticaBold, color: rgb(0.1, 0.4, 0.82)
      });
      yPos -= 40;

      // === TITLE ===
      drawTextBlock(options.title, 14, helveticaBold, 20);
      yPos -= 10;

      // === DATE ===
      const dateStr = (options.date || new Date()).toLocaleDateString('de-DE', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
      drawTextBlock(`Datum: ${dateStr}`, 9, helveticaFont, 13, rgb(0.4, 0.4, 0.4));
      yPos -= 20;

      // === CONTENT ===
      const lines = options.content.split('\n');
      for (const line of lines) {
        if (line.trim() === '') {
          yPos -= 6;
          continue;
        }

        if (isHeading(line.trim())) {
          yPos -= 4;
          drawTextBlock(line, 10, helveticaBold, 14);
          yPos -= 2;
        } else {
          drawTextBlock(line, 9, helveticaFont, 13);
        }
      }

      const pdfBytes = await pdfDoc.save();
      return pdfBytes;

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  },
  
  downloadPDF: async (pdfBytes: Uint8Array, filename: string) => {
    try {
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  },
  
  generateSEPAMandat: async (data: {
    payerName: string;
    payerAddressLines: string[];
    iban: string;
    bic: string;
    mandateReference: string;
    mandateLocation?: string;
    signatureImage?: string;
    signatureDate?: Date;
    signatoryName?: string;
    additionalInfo?: string;
  }): Promise<Uint8Array> => {
    try {
      const templatePath = '/contracts/Lastschriftmandat.pdf';
      const cacheKey = `_sepaTemplateBytes_${templatePath}`;
      let templateBytes = (pdfService as any)[cacheKey] as Uint8Array | undefined;

      if (!templateBytes) {
        const response = await fetch(templatePath);
        if (!response.ok) {
          throw new Error(`SEPA-Vorlage konnte nicht geladen werden: ${response.status} ${response.statusText}`);
        }
        const buffer = await response.arrayBuffer();
        templateBytes = new Uint8Array(buffer);
        (pdfService as any)[cacheKey] = templateBytes;
      }

      const pdfDoc = await PDFDocument.load(templateBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const pages = pdfDoc.getPages();

      type PlaceholderRect = { pageIndex: number; x: number; y: number; width: number; height: number };
      const placeholderPositions: Record<string, PlaceholderRect[]> = {};

      const placeholderLookup: Record<string, string> = {
        '{Mandatsreferenz}': 'mandateReference',
        '{Name}': 'payerName',
        '{BIC}': 'bic',
        '{IBAN}': 'iban',
        '{Name, Anschrift}': 'address',
        '{Ort}': 'location',
        '{d}': 'day',
        '{m}': 'month',
        '{yyyy}': 'year',
        '{Unterschrift}': 'signature',
      };

      for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        const currentPage = pages[pageIndex];
        const annotsRef = currentPage.node.get(PDFName.of('Annots'));
        if (!annotsRef) continue;
        const annots = pdfDoc.context.lookup(annotsRef);
        if (!(annots instanceof PDFArray)) continue;

        for (let i = annots.size() - 1; i >= 0; i--) {
          const annotRef = annots.get(i);
          const annot = pdfDoc.context.lookup(annotRef);
          if (!annot || !(annot instanceof PDFDict)) continue;

          let placeholderKey: string | undefined;
          const contents = annot.get(PDFName.of('Contents'));
          if (contents instanceof PDFString || contents instanceof PDFHexString) {
            const normalized = contents.decodeText().replace(/\s+/g, ' ').trim();
            placeholderKey = placeholderLookup[normalized];
          }

          const rect = annot.get(PDFName.of('Rect'));
          if (placeholderKey && rect instanceof PDFArray && rect.size() === 4) {
            const x1 = (rect.get(0) as PDFNumber).asNumber();
            const y1 = (rect.get(1) as PDFNumber).asNumber();
            const x2 = (rect.get(2) as PDFNumber).asNumber();
            const y2 = (rect.get(3) as PDFNumber).asNumber();
            const entry: PlaceholderRect = {
              pageIndex,
              x: x1,
              y: y1,
              width: x2 - x1,
              height: y2 - y1,
            };
            if (!placeholderPositions[placeholderKey]) {
              placeholderPositions[placeholderKey] = [];
            }
            placeholderPositions[placeholderKey].push(entry);
          }

          if (placeholderKey) {
            annots.remove(i);
          }
        }
      }

      const signatureDate = data.signatureDate || new Date();
      const dayString = signatureDate.getDate().toString().padStart(2, '0');
      const monthString = (signatureDate.getMonth() + 1).toString().padStart(2, '0');
      const yearString = signatureDate.getFullYear().toString();
      const drawText = (
        pageIndex: number,
        rect: PlaceholderRect,
        text: string,
        size: number,
        options: {
          font?: any;
          align?: 'left' | 'center';
          verticalAlign?: 'top' | 'center';
          letterSpacing?: number;
          characterBoxes?: number;
          shiftX?: number;
        } = {}
      ) => {
        const page = pages[pageIndex];
        const font = options.font || helveticaFont;
        const align = options.align || 'left';
        const verticalAlign = options.verticalAlign || 'top';
        const letterSpacing = options.letterSpacing ?? 0;
        const characterBoxes = options.characterBoxes ?? 0;
        const adjustedText = characterBoxes > 0 && letterSpacing !== 0 ? text.padEnd(characterBoxes, ' ') : text;
        const textWidth = characterBoxes > 0 && letterSpacing !== 0
          ? characterBoxes * size + (characterBoxes - 1) * letterSpacing
          : font.widthOfTextAtSize(adjustedText, size);
        let x = rect.x;
        if (align === 'center') {
          x = rect.x + Math.max((rect.width - textWidth) / 2, 0);
        }
        x += options.shiftX ?? 0;
        const y =
          verticalAlign === 'center'
            ? rect.y + Math.max((rect.height - size) / 2, 0)
            : rect.y + rect.height - size - 1;
        page.drawText(adjustedText, {
          x,
          y,
          size,
          font,
          color: rgb(0, 0, 0),
        });
      };

      const addressLines = data.payerAddressLines
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      const locationLine = data.mandateLocation || addressLines[addressLines.length - 1] || '';

      if (placeholderPositions.mandateReference) {
        placeholderPositions.mandateReference.forEach((rect) =>
          drawText(rect.pageIndex, rect, data.mandateReference, 10, {
            font: helveticaBold,
          align: 'center',
            verticalAlign: 'center',
          })
        );
      }

      if (placeholderPositions.payerName) {
        placeholderPositions.payerName.forEach((rect) =>
          drawText(rect.pageIndex, rect, data.payerName, 8)
        );
      }

      const drawCharactersInBoxes = (
        rect: PlaceholderRect,
        text: string,
        size: number,
        totalBoxes: number
      ) => {
        const page = pages[rect.pageIndex];
        const characters = text.split('');
        const boxWidth = rect.width / totalBoxes;
        const baseY = rect.y + (rect.height - size) / 2;
        characters.forEach((char, index) => {
          if (index >= totalBoxes) {
            return;
          }
          const charWidth = helveticaFont.widthOfTextAtSize(char, size);
          const charX = rect.x + index * boxWidth + (boxWidth - charWidth) / 2;
          page.drawText(char, {
            x: charX,
            y: baseY,
            size,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        });
      };

      if (placeholderPositions.bic) {
        placeholderPositions.bic.forEach((rect) =>
          drawCharactersInBoxes(rect, data.bic, 7, 11)
        );
      }

      if (placeholderPositions.iban) {
        placeholderPositions.iban.forEach((rect) =>
          drawCharactersInBoxes(rect, data.iban, 8, data.iban.length)
        );
      }

      if (placeholderPositions.address) {
        const addressString = addressLines.join(', ');
        placeholderPositions.address.forEach((rect) =>
          drawText(rect.pageIndex, rect, addressString, 8, { align: 'center' })
        );
      }

      if (placeholderPositions.location && locationLine) {
        placeholderPositions.location.forEach((rect) =>
          drawText(rect.pageIndex, rect, locationLine, 8)
        );
      }

      const dateDrawOptions = { align: 'center' as const, verticalAlign: 'center' as const };
      if (placeholderPositions.day && placeholderPositions.day.length > 0) {
        placeholderPositions.day.forEach((rect, index) => {
          const offset = index === 0 ? -1 : index === 1 ? 0 : 0;
          drawText(rect.pageIndex, rect, dayString, 8, {
            ...dateDrawOptions,
            shiftX: offset,
          });
        });
      }

      if (placeholderPositions.month) {
        placeholderPositions.month.forEach((rect) =>
          drawText(rect.pageIndex, rect, monthString, 8, dateDrawOptions)
        );
      }

      if (placeholderPositions.year) {
        placeholderPositions.year.forEach((rect) =>
          drawText(rect.pageIndex, rect, yearString, 8, dateDrawOptions)
        );
      }


      if (data.signatureImage && placeholderPositions.signature) {
        try {
          const signatureBase64 = data.signatureImage.includes('base64,')
            ? data.signatureImage.split('base64,')[1]
            : data.signatureImage;

          const signatureBytes = Uint8Array.from(atob(signatureBase64), (c) => c.charCodeAt(0));
          const signatureImage = await pdfDoc.embedPng(signatureBytes);

          placeholderPositions.signature.forEach((rect) => {
            const page = pages[rect.pageIndex];
            const widthScale = rect.width / signatureImage.width;
            const heightScale = rect.height / signatureImage.height;
            const scale = Math.min(widthScale, heightScale, 1);
            const drawWidth = signatureImage.width * scale;
            const drawHeight = signatureImage.height * scale;
            const drawX = rect.x + (rect.width - drawWidth) / 2;
            const drawY = rect.y + (rect.height - drawHeight) / 2;

            page.drawImage(signatureImage, {
              x: drawX,
              y: drawY,
              width: drawWidth,
              height: drawHeight,
            });

            if (data.signatoryName) {
              page.drawText(data.signatoryName, {
                x: rect.x,
                y: rect.y - 12,
                size: 8,
                font: helveticaFont,
                color: rgb(0, 0, 0),
              });
            }
          });
        } catch (error) {
          console.error('Error embedding signature image into SEPA mandate PDF:', error);
        }
      }

      const pdfBytes = await pdfDoc.save();
      return pdfBytes;

    } catch (error) {
      console.error('Error generating SEPA mandate PDF:', error);
      throw error;
    }
  },

  generateDeletionCertificate: async (data: DeletionCertificateData): Promise<Uint8Array> => {
    try {
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const PAGE_WIDTH = 595;
      const PAGE_HEIGHT = 842;
      const MARGIN = 56;
      const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
      const VALUE_X = MARGIN + 190;

      const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      let y = PAGE_HEIGHT - MARGIN;

      const draw = (
        text: string,
        size: number,
        f: typeof font = font,
        color = rgb(0.1, 0.1, 0.12),
        x: number = MARGIN
      ) => {
        page.drawText(text, { x, y, size, font: f, color });
      };

      const fmt = (iso?: string): string => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('de-DE', { dateStyle: 'long', timeStyle: 'medium' });
      };

      const wrapText = (text: string, f: typeof font, size: number, maxW: number): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let current = '';
        for (const word of words) {
          const test = current ? `${current} ${word}` : word;
          if (f.widthOfTextAtSize(test, size) > maxW && current) {
            lines.push(current);
            current = word;
          } else {
            current = test;
          }
        }
        if (current) lines.push(current);
        return lines;
      };

      // === Letterhead ===
      draw(data.businessName || 'Unternehmen', 15, fontBold, rgb(0.1, 0.4, 0.82));
      y -= 18;
      if (data.businessAddress) {
        draw(data.businessAddress, 9, font, rgb(0.45, 0.45, 0.45));
        y -= 14;
      }
      y -= 20;

      // === Title ===
      draw('Löschungsbestätigung', 18, fontBold);
      y -= 22;
      draw('Nachweis der Löschung personenbezogener Daten gemäß Art. 17 DSGVO', 10, font, rgb(0.4, 0.4, 0.4));
      y -= 20;
      draw(`Beleg-Nr.: ${data.receiptId}`, 9, font, rgb(0.55, 0.55, 0.55));
      y -= 30;

      page.drawLine({
        start: { x: MARGIN, y },
        end: { x: PAGE_WIDTH - MARGIN, y },
        thickness: 1,
        color: rgb(0.85, 0.85, 0.85),
      });
      y -= 26;

      const section = (title: string) => {
        draw(title, 12, fontBold);
        y -= 18;
      };

      const row = (label: string, value: string) => {
        draw(label, 10, font, rgb(0.45, 0.45, 0.45));
        draw(value, 10, fontBold, rgb(0.1, 0.1, 0.12), VALUE_X);
        y -= 18;
      };

      section('Betroffene Person');
      row('Name', data.customerName || 'Unbekannt');
      row('Telefonnummer', data.customerPhone || '—');
      row('E-Mail', data.customerEmail || '—');
      y -= 14;

      section('Zeitliche Angaben');
      row('Datensatz erstellt am', fmt(data.customerCreatedAt));
      row('Datensatz gelöscht am', fmt(data.deletedAt));
      y -= 14;

      section('Gelöschte Datensätze');
      row('Kundenprofil', 'Name, Telefonnummer, E-Mail');
      row('Termine', `${data.deletedAppointments}`);
      row('Gesprächsverläufe', `${data.deletedConversations}`);
      y -= 26;

      page.drawLine({
        start: { x: MARGIN, y },
        end: { x: PAGE_WIDTH - MARGIN, y },
        thickness: 1,
        color: rgb(0.85, 0.85, 0.85),
      });
      y -= 24;

      const footerNote =
        'Diese Bestätigung dient als Nachweis der vollständigen und unwiderruflichen Löschung aller personenbezogenen Daten dieser Person (Kundenprofil, Terminhistorie, Gesprächsverläufe) gemäß Art. 17 DSGVO (Recht auf Löschung). Automatisch ausgestellt durch das TimeGlobe Anbieterportal.';
      for (const line of wrapText(footerNote, font, 8.5, CONTENT_WIDTH)) {
        draw(line, 8.5, font, rgb(0.55, 0.55, 0.55));
        y -= 12;
      }

      return await pdfDoc.save();
    } catch (error) {
      console.error('Error generating deletion certificate PDF:', error);
      throw error;
    }
  },
};

export default pdfService;